import React, { useRef, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export default function AssetPreview({ object }) {
  const canvasWrapperRef = useRef();
  const sceneRef = useRef();
  const cameraRef = useRef();
  const rendererRef = useRef();
  const controlsRef = useRef();
  const objectGroupRef = useRef();

  const render = () => {
    const renderer = rendererRef.current;
    const camera = cameraRef.current;
    const scene = sceneRef.current;
    renderer.render(scene, camera);
  };

  const resize = useCallback(() => {
    const bounds = canvasWrapperRef.current.getBoundingClientRect();
    cameraRef.current.aspect = bounds.width / bounds.height;
    cameraRef.current.updateProjectionMatrix();
    rendererRef.current.setSize(bounds.width, bounds.height);
    render();
  }, []);

  useEffect(() => {
    const scene = new THREE.Scene();
    scene.current = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 2000);
    camera.position.z = 50;
    camera.position.y = 20;
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputEncoding = THREE.sRGBEncoding;
    canvasWrapperRef.current.appendChild(renderer.domElement);

    // Hemi light
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.6);
    hemiLight.color.setHSL(0.6, 1, 0.6);
    hemiLight.groundColor.setHSL(0.095, 1, 0.75);
    scene.add(hemiLight);

    // Directional Light
    const dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.color.setHSL(0.1, 1, 0.95);
    dirLight.position.set(-200, 300, -200);
    // dirLight.position.multiplyScalar(30);
    scene.add(dirLight);

    // Ground
    const groundGeo = new THREE.PlaneBufferGeometry(10000, 10000);
    const groundMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
    groundMat.color.setHSL(0.095, 1, 0.75);
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.position.y = -0.1;
    ground.rotation.x = -Math.PI / 2;
    scene.add(ground);

    const gridHelper = new THREE.GridHelper(1000, 200);
    scene.add(gridHelper);

    const objectGroup = new THREE.Group();
    scene.add(objectGroup);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.minDistance = 10;
    controls.maxDistance = 500;
    controls.addEventListener('change', render);

    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    controlsRef.current = controls;
    objectGroupRef.current = objectGroup;
    window.addEventListener('resize', resize);
    resize();
    return () => {
      window.removeEventListener('resize', resize);
      controls.dispose();
    };
  }, [resize]);

  useEffect(() => {
    const objectGroup = objectGroupRef.current;

    // Remove everything from the object group
    while (objectGroup.children.length) {
      objectGroup.remove(objectGroup.children[0]);
    }

    if (object) {
      let newObject = object.clone();
      if (newObject.type === 'Group') {
        // Each child is a different material.
        for (const mesh of newObject.children) {
          const instancedMesh = new THREE.InstancedMesh(mesh.geometry, mesh.material, 1);
          const m = new THREE.Matrix4();
          m.setPosition(0, 0, 0);
          // m.setPosition(Math.random() * 2.0, 0, Math.random() * 2.0);
          instancedMesh.setMatrixAt(0, m);
          objectGroup.add(instancedMesh);
        }
      } else if (newObject.type === 'Mesh') {
        const instancedMesh = new THREE.InstancedMesh(newObject.geometry, newObject.material, 1);
        const m = new THREE.Matrix4();
        m.setPosition(0, 1, 0);
        instancedMesh.setMatrixAt(0, m);
        objectGroup.add(instancedMesh);
      } else {
        // Unsupported object type.
      }
    }

    render();
  }, [object]);

  return <div className="canvas__wrapper" ref={canvasWrapperRef} />;
}
